import { takeLatest, call, put } from 'redux-saga/effects';

import api from '../api';
import {
    listVehiclesRequest,
    listVehiclesSuccess,
    listVehiclesFailure,
    listVehiclesDeliveryRequest,
    listVehiclesDeliverySuccess,
    listVehiclesDeliveryFailure,
    getVehicleRequest,
    getVehicleSuccess,
    getVehicleFailure,
    updateVehicleRequest,
    updateVehicleSuccess,
    updateVehicleFailure,
    getMediaTypesRequest,
    getMediaTypesSuccess,
    getMediaTypesFailure,
    MediaType,
    Media,
    uploadMediaRequest,
    uploadMediaSuccess,
    uploadMediaFailure,
    uploadDocumentRequest,
    uploadDocumentSuccess,
    uploadDocumentFailure,
    Document,
    getInspectionsRequest,
    getInspectionsSuccess,
    getInspectionsFailure,
    InspectionSection,
    Vehicle,
    Store,
    getStoreLocationsRequest,
    getStoreLocationsSuccess,
    getStoreLocationsFailure,
} from './actions';

// API calls
const listVehicleApi = async (filters: string, vehicle_fields: string, excludes: string): Promise<Vehicle[]> => {
    let params = new URLSearchParams({
        filters: filters || '',
        excludes: excludes || '',
        vehicle_fields: vehicle_fields
    }).toString();

    const response = await api.get('/main/api/vehicles/list/?' + params);
    return response.data;
};

const updateVehicleApi = async (vehicle: Vehicle, update_fields: string[], vehicle_fields?: string): Promise<Vehicle> => {
    let params = "";
    if (vehicle_fields) {
        params = "?"+ new URLSearchParams({
            vehicle_fields: vehicle_fields
        }).toString();
    };

    let data = [Object.fromEntries(Object.entries(vehicle).filter(([key]) => update_fields.includes(key)))];

    const response = await api.post('/main/api/vehicles/post/' + params, data);
    return response.data[0];
};

const getMediaTypesApi = async (): Promise<MediaType[]> => {
    const response = await api.get('/main/api/options/mediatypes/');
    return response.data;
};

const uploadMediaApi = async (payload: { vehicle_id: number; section_id: number; files: FileList; }): Promise<Media> => {
    const formData = new FormData();
    formData.append("id", payload.vehicle_id.toString());
    formData.append("section_id", payload.section_id.toString());
    Array.from(payload.files).forEach((file) => {
        formData.append("files", file);
    });

    const response = await api.post("/main/api/vehicles/upload-media/", formData, {
        headers: {
            "Content-Type": "multipart/form-data",
        },
    });
    return response.data;
};

const uploadDocumentApi = async (payload: {
    vehicle_id: number;
    files: FileList;
    filenames: string[];
    type: string;
}): Promise<Document> => {
    const formData = new FormData();
    formData.append("id", payload.vehicle_id.toString());
    formData.append("type", payload.type);
    payload.filenames.forEach((filename) => {
        formData.append("filenames", filename);
    });
    Array.from(payload.files).forEach((file) => {
        formData.append("files", file);
    });
    const response = await api.post("/main/api/vehicles/upload-document/", formData, {
        headers: {
            "Content-Type": "multipart/form-data",
        },
    });
    return response.data;
};

const getInspectionsApi = async (): Promise<InspectionSection[]> => {
    const response = await api.get('/main/api/options/inspections-sections/');
    return response.data;
};

const getStoreLocationsApi = async (): Promise<Store[]> => {
    const response = await api.get('/main/api/stores/list/');
    return response.data;
};

// Sagas
function* handleListVehicle(action: ReturnType<typeof listVehiclesRequest>) {
    try {
        const data: Vehicle[] = yield call(
            listVehicleApi,
            action.payload.filters,
            action.payload.vehicle_fields,
            action.payload.excludes
        );
        yield put(listVehiclesSuccess({vehicles: data, save_as: action.payload.save_as}));
    } catch (error: any) {
        yield put(listVehiclesFailure(error.response?.data || 'Failed to list vehicles'));
    }
}

function* handleGetVehicle(action: ReturnType<typeof getVehicleRequest>) {
    try {
        const filters = action.payload.id ? 'id:' + action.payload.id : 'vin:' + action.payload.vin;
        const data: Vehicle[] = yield call(
            listVehicleApi,
            filters,
            action.payload.vehicle_fields,
            '',
        );
        yield put(getVehicleSuccess(data.length ? data[0] : {id: -1}));
    } catch (error: any) {
        yield put(getVehicleFailure(error.response?.data || 'Failed to list vehicles'));
    }
}

function* handleListVehicleDelivery(action: ReturnType<typeof listVehiclesDeliveryRequest>) {
    try {
        let filters = 'is_purchased:True,is_sold:False,status:pending_delivery,delivery_date:null';

        if (action.payload.date_from) {
            filters += ',delivery_date__date__gte:' + action.payload.date_from;
        }

        if (action.payload.date_to) {
            filters += ',delivery_date__date__lte:' + action.payload.date_to;
        }

        const data: Vehicle[] = yield call(
            listVehicleApi,
            filters,
            action.payload.vehicle_fields,
            'status:sold',
        );
        yield put(listVehiclesDeliverySuccess(data));
    } catch (error: any) {
        yield put(listVehiclesDeliveryFailure(error.response?.data || 'Failed to list vehicles'));
    }
}

function* handleUpdateVehicle(action: ReturnType<typeof updateVehicleRequest>) {
    try {
        const data: Vehicle = yield call(
            updateVehicleApi,
            action.payload.vehicle,
            action.payload.update_fields,
            action.payload.vehicle_fields,
        );
        if (action.payload.cb) {
            action.payload.cb(data);
        }
        yield put(updateVehicleSuccess(data));
    } catch (error: any) {
        yield put(updateVehicleFailure(error.response?.data || 'Failed to update vehicles'));
    }
}

function* handleGetMediaTypes() {
    try {
        const data: MediaType[] = yield call(getMediaTypesApi);
        yield put(getMediaTypesSuccess(data));
    } catch (error: any) {
        yield put(getMediaTypesFailure(error.response?.data || 'Failed to get media types'));
    }
}

function* handleUploadMedia(action: ReturnType<typeof uploadMediaRequest>) {
    try {
        const data: Media[] = yield call(uploadMediaApi, action.payload);
        yield put(uploadMediaSuccess({vehicle_id: action.payload.vehicle_id, medias: data}));
    } catch (error: any) {
        yield put(uploadMediaFailure(error.response?.data || 'Failed to upload media'));
    }
}

function* handleUploadDocument(action: ReturnType<typeof uploadDocumentRequest>) {
    try {
        const data: Document[] = yield call(uploadDocumentApi, action.payload);
        yield put(uploadDocumentSuccess({vehicle_id: action.payload.vehicle_id, documents: data}));
    } catch (error: any) {
        yield put(uploadDocumentFailure(error.response?.data || 'Failed to upload document'));
    }
}

function* handleGetInspections() {
    try {
        const data: InspectionSection[] = yield call(getInspectionsApi);
        yield put(getInspectionsSuccess(data));
    } catch (error: any) {
        yield put(getInspectionsFailure(error.response?.data || 'Failed to get inspections'));
    }
}

function* handleGetStoreLocations() {
    try {
        const data: Store[] = yield call(getStoreLocationsApi);
        yield put(getStoreLocationsSuccess(data));
    } catch (error: any) {
        yield put(getStoreLocationsFailure(error.response?.data || 'Failed to get store locations'));
    }
}

// Watcher sagas
export function* watchVehiclesSaga() {
    yield takeLatest(getVehicleRequest.type, handleGetVehicle);
    yield takeLatest(listVehiclesRequest.type, handleListVehicle);
    yield takeLatest(listVehiclesDeliveryRequest.type, handleListVehicleDelivery);
    yield takeLatest(updateVehicleRequest.type, handleUpdateVehicle);
    yield takeLatest(getMediaTypesRequest.type, handleGetMediaTypes);
    yield takeLatest(uploadMediaRequest.type, handleUploadMedia);
    yield takeLatest(uploadDocumentRequest.type, handleUploadDocument);
    yield takeLatest(getInspectionsRequest.type, handleGetInspections);
    yield takeLatest(getStoreLocationsRequest.type, handleGetStoreLocations);
}